import { useState } from "react";
import {
  CheckCircle2, AlertTriangle, Info, XCircle, ChevronDown, ChevronRight,
} from "lucide-react";
import { Drawer, DrawerBody, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/ui/progress-bar";
import { useJob } from "./api";
import { formatDateTime } from "@/lib/utils";
import type { JobProgress } from "./schemas";
import {
  translateJobResult,
  translateJobError,
  type Bullet,
  type BulletTone,
} from "./jobResultTranslator";

type Props = {
  jobId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

const ACTION_LABEL: Record<string, string> = {
  reconcile_contract: "Reconciliar contrato",
  reconcile_full_router: "Reconciliar router completo",
  delete_contract: "Borrar contrato del router",
  reconcile_router_firewall: "Recrear reglas de firewall",
};

export function JobDetailDrawer({ jobId, open, onOpenChange }: Props) {
  const { data: job, isLoading } = useJob(jobId);
  const [showRawResult, setShowRawResult] = useState(false);
  const [showRawPayload, setShowRawPayload] = useState(false);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} size="lg">
      <DrawerHeader>
        <DrawerTitle>Detalle del job</DrawerTitle>
      </DrawerHeader>
      <DrawerBody>
        {isLoading || !job ? (
          <div className="py-8 text-center text-xs text-muted-foreground">Cargando…</div>
        ) : (
          <div className="flex flex-col gap-4 text-xs">
            {/* Meta top */}
            <div className="grid grid-cols-2 gap-2">
              <Meta
                label="Acción"
                value={
                  <span className="font-medium">
                    {ACTION_LABEL[job.action] ?? job.action}
                  </span>
                }
              />
              <Meta label="Status" value={<Badge>{job.status}</Badge>} />
              <Meta label="Intento" value={`${job.attempt} / ${job.max_attempts}`} />
              <Meta
                label="Router"
                value={<span className="font-mono text-[10px]">{job.router_id}</span>}
              />
              <Meta label="Encolado" value={formatDateTime(job.enqueued_at)} />
              {job.started_at && (
                <Meta label="Iniciado" value={formatDateTime(job.started_at)} />
              )}
              {job.completed_at && (
                <Meta label="Completado" value={formatDateTime(job.completed_at)} />
              )}
              {job.next_retry_at && (
                <Meta label="Próximo retry" value={formatDateTime(job.next_retry_at)} />
              )}
              <Meta
                label="ID"
                value={<span className="font-mono text-[10px]">{job.id}</span>}
              />
            </div>

            {/* dev119 — Progreso en vivo (solo cuando running) */}
            {job.status === "running" && job.progress && (
              <ProgressSection progress={job.progress} />
            )}

            {/* Qué se hizo */}
            <section>
              <h4 className="mb-2 text-[10px] uppercase text-muted-foreground font-medium tracking-wide">
                Qué se hizo
              </h4>
              <BulletList bullets={translateJobResult(job)} />
            </section>

            {/* Error legible (además del bullet de error en "Qué se hizo") */}
            {job.error && job.status === "error" && (
              <section className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <h4 className="mb-1 text-[10px] uppercase text-destructive font-medium tracking-wide">
                  Detalle del error
                </h4>
                {(() => {
                  const b = translateJobError(job.error, job.error_code);
                  return (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs font-medium text-destructive">{b.text}</p>
                      {b.detail && (
                        <p className="text-[11px] text-destructive/80">{b.detail}</p>
                      )}
                      <p className="mt-1 text-[10px] text-muted-foreground font-mono">
                        original: {job.error}
                        {job.error_code && ` · code: ${job.error_code}`}
                      </p>
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Payload (colapsable) */}
            <section>
              <button
                type="button"
                onClick={() => setShowRawPayload((v) => !v)}
                className="flex w-full items-center gap-1 text-[10px] uppercase text-muted-foreground font-medium tracking-wide hover:text-foreground"
              >
                {showRawPayload ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                Payload (detalle técnico)
              </button>
              {showRawPayload && (
                <pre className="mt-1 rounded bg-muted/30 p-2 text-[10px] font-mono overflow-auto max-h-60">
                  {JSON.stringify(job.payload, null, 2)}
                </pre>
              )}
            </section>

            {/* Result JSON crudo (colapsable) */}
            {job.result && (
              <section>
                <button
                  type="button"
                  onClick={() => setShowRawResult((v) => !v)}
                  className="flex w-full items-center gap-1 text-[10px] uppercase text-muted-foreground font-medium tracking-wide hover:text-foreground"
                >
                  {showRawResult ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Resultado crudo (JSON técnico)
                </button>
                {showRawResult && (
                  <pre className="mt-1 rounded bg-muted/30 p-2 text-[10px] font-mono overflow-auto max-h-96">
                    {JSON.stringify(job.result, null, 2)}
                  </pre>
                )}
              </section>
            )}
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
}

function BulletList({ bullets }: { bullets: Bullet[] }) {
  if (bullets.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">Sin información registrada.</p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {bullets.map((b, i) => (
        <BulletItem key={i} bullet={b} />
      ))}
    </ul>
  );
}

function BulletItem({ bullet }: { bullet: Bullet }) {
  const { tone } = bullet;
  return (
    <li className="flex items-start gap-2">
      <ToneIcon tone={tone} />
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className={toneTextClass(tone)}>{bullet.text}</span>
        {bullet.detail && (
          <span className="text-[11px] text-muted-foreground">{bullet.detail}</span>
        )}
      </div>
    </li>
  );
}

function ToneIcon({ tone }: { tone: BulletTone }) {
  const common = "shrink-0 mt-0.5";
  switch (tone) {
    case "ok":
      return <CheckCircle2 size={13} className={`${common} text-emerald-500`} />;
    case "warn":
      return <AlertTriangle size={13} className={`${common} text-amber-500`} />;
    case "error":
      return <XCircle size={13} className={`${common} text-destructive`} />;
    case "info":
    default:
      return <Info size={13} className={`${common} text-muted-foreground`} />;
  }
}

/**
 * dev119 — Bloque de progreso en vivo. Solo se muestra cuando el job está
 * `running` y tiene `progress` populated (= jobs batch tipo
 * reconcile_full_router con muchos contratos).
 *
 * Muestra: barra de progreso con %, frase actual ("Procesando CON-XXX —
 * Cliente"), contadores OK/error, y tabla de últimos 10 items procesados.
 */
function ProgressSection({ progress }: { progress: JobProgress }) {
  const pct = progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;
  const phaseLabel: Record<JobProgress["phase"], string> = {
    firewall: "Reglas de firewall",
    contracts: "Contratos",
    done: "Completado",
  };
  return (
    <section className="rounded-md border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] uppercase font-medium tracking-wide text-primary">
          Progreso en vivo · {phaseLabel[progress.phase]}
        </h4>
        <span className="text-[10px] font-mono text-muted-foreground">
          {progress.current} / {progress.total} ({pct}%)
        </span>
      </div>

      <ProgressBar
        value={progress.current}
        max={progress.total}
        size="md"
        pulse
        className="mb-2"
      />

      {progress.current_label && (
        <p className="text-[11px] text-foreground mb-2 truncate" title={progress.current_label}>
          → {progress.current_label}
        </p>
      )}

      <div className="flex gap-3 text-[10px]">
        <span className="text-emerald-700 dark:text-emerald-400">
          ✓ {progress.items_ok} OK
        </span>
        {progress.items_error > 0 && (
          <span className="text-destructive">
            ✗ {progress.items_error} con error
          </span>
        )}
      </div>

      {progress.recent && progress.recent.length > 0 && (
        <div className="mt-3 rounded border bg-background/60">
          <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground border-b">
            Últimos procesados
          </div>
          <ul className="divide-y">
            {progress.recent.slice().reverse().map((r, i) => (
              <li
                key={`${r.codigo}-${i}`}
                className="flex items-center gap-2 px-2 py-1 text-[10px]"
              >
                {r.ok ? (
                  <CheckCircle2 size={10} className="text-emerald-500 shrink-0" />
                ) : (
                  <XCircle size={10} className="text-destructive shrink-0" />
                )}
                <span className="font-mono">{r.codigo}</span>
                <span className="truncate text-muted-foreground">— {r.cliente}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function toneTextClass(tone: BulletTone): string {
  switch (tone) {
    case "ok":
      return "text-xs text-foreground";
    case "warn":
      return "text-xs text-amber-700 dark:text-amber-400";
    case "error":
      return "text-xs text-destructive font-medium";
    case "info":
    default:
      return "text-xs text-muted-foreground";
  }
}
